// src/pages/Dashboard/CashMoney.jsx
import React, { useState, useEffect, useContext } from 'react';
import { ThemeContext } from '../../context/ThemeContext';
import { useNotification } from '../../context/NotificationProvider';
import {
  DollarSign, TrendingUp, Wallet, AlertCircle, Calendar,
  Download, RefreshCw, Search, Filter, Users, Truck,
  CheckCircle, Clock, X, FileSpreadsheet
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
  Tooltip
} from 'recharts';
import { getData, postData } from '../../services/Fetchnodeservices';


const CashMoney = () => {
  const { theme, currentTheme } = useContext(ThemeContext);
  const { showSuccess, showError } = useNotification();
  const isDark = currentTheme === 'dark';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [range, setRange] = useState('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositMode, setDepositMode] = useState('CASH');

  // Theme colors
  const bg = isDark ? '#1a1d2e' : '#f5f7fb';
  const cardBg = isDark ? '#242842' : '#ffffff';
  const textColor = isDark ? '#e5e7eb' : '#1f2937';
  const subText = isDark ? '#9ca3af' : '#6b7280';
  const borderColor = isDark ? '#374151' : '#e5e7eb';
  const accent = '#6c63ff';

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('range', range);
      if (range === 'custom' && startDate && endDate) {
        params.append('startDate', startDate);
        params.append('endDate', endDate);
      }

      const res = await getData(`orders/admin/cashmoney/summary?${params.toString()}`);

      if (res?.status) {
        setData(res.data);
      } else {
        showError(res?.message || 'Failed to load cash data');
      }
    } catch (err) {
      console.error('CashMoney fetch error:', err);
      showError('Network error while loading cash data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [range]);

  // Apply custom date range
  const applyCustomRange = () => {
    if (!startDate || !endDate) {
      showError('Please select both start and end dates');
      return;
    }
    setRange('custom');
    setTimeout(fetchData, 100);
  };

  // Mark orders as deposited
  const markAsDeposited = async () => {
    if (selectedOrders.length === 0) {
      showError('Please select at least one order');
      return;
    }

    try {
      const res = await postData('/admin/cashmoney/mark-deposited', {
        orderIds: selectedOrders,
        mode: depositMode,
      });

      if (res?.status) {
        showSuccess(res.message);
        setSelectedOrders([]);
        setShowDepositModal(false);
        fetchData();
      } else {
        showError(res?.message || 'Failed to mark orders');
      }
    } catch (err) {
      showError('Network error while marking orders');
    }
  };

  // Toggle order selection
  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  // Select all pending
  const selectAllPending = () => {
    const pending = (data?.orderDetails || [])
      .filter(o => !o.cash_collected_at && !['cancelled', 'declined'].includes(o.order_status_driver))
      .map(o => o.order_id);
    setSelectedOrders(pending);
  };

  // Export to CSV
  const exportCSV = () => {
    if (!data?.orderDetails?.length) {
      showError('No data to export');
      return;
    }
    const rows = [
      ['Order ID', 'Date', 'Driver', 'Restaurant', 'Receiver', 'Amount', 'Status', 'Collected At', 'Mode'],
      ...data.orderDetails.map(o => [
        o.order_id,
        new Date(o.created_at).toLocaleString('en-IN'),
        o.driver_name || 'Unassigned',
        o.restaurant_name || 'N/A',
        o.receiver_name || 'N/A',
        o.final_amount,
        o.cash_collected_at ? 'Collected' : 'Pending',
        o.cash_collected_at ? new Date(o.cash_collected_at).toLocaleString('en-IN') : 'N/A',
        o.payment_received_mode || 'N/A',
      ]),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cashmoney_${range}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showSuccess('CSV exported successfully');
  };

  // Format currency
  const fmt = (n) => {
    const v = Number(n || 0);
    return '₹' + v.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  };

  const chartColors = isDark
    ? ['#6c63ff', '#10b981', '#f59e0b', '#ef4444', '#3b82f6']
    : ['#6c63ff', '#10b981', '#f59e0b', '#ef4444', '#3b82f6'];

  if (loading && !data) {
    return (
      <div style={{ padding: 24, color: textColor }}>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', color: accent }} />
          <p style={{ marginTop: 16 }}>Loading cash tracking data...</p>
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const summary = data?.summary || {};

  return (
    <div style={{ padding: 24, backgroundColor: bg, minHeight: '100vh', color: textColor }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Wallet size={28} color={accent} />
            CashMoney — COD Tracker
          </h1>
          <p style={{ margin: '4px 0 0', color: subText, fontSize: 14 }}>
            Track cash on delivery payments — day, month, or custom date range
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={fetchData} disabled={loading}
            style={btnStyle(isDark, accent)}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
            Refresh
          </button>
          <button onClick={exportCSV} style={btnStyle(isDark, '#10b981')}>
            <FileSpreadsheet size={16} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Date Range Selector */}
      <div style={{
        backgroundColor: cardBg, borderRadius: 12, padding: 16,
        border: `1px solid ${borderColor}`, marginBottom: 20,
      }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Calendar size={18} color={subText} />
          <span style={{ fontSize: 14, fontWeight: 600, marginRight: 8 }}>Range:</span>

          {[
            { v: 'today', l: 'Today' },
            { v: 'yesterday', l: 'Yesterday' },
            { v: 'this_month', l: 'This Month' },
            { v: 'last_month', l: 'Last Month' },
            { v: 'custom', l: 'Custom' },
          ].map(r => (
            <button key={r.v} onClick={() => setRange(r.v)}
              style={{
                padding: '8px 14px', borderRadius: 8,
                backgroundColor: range === r.v ? accent : 'transparent',
                color: range === r.v ? '#fff' : textColor,
                border: `1px solid ${range === r.v ? accent : borderColor}`,
                cursor: 'pointer', fontSize: 13, fontWeight: 500,
              }}>
              {r.l}
            </button>
          ))}

          {range === 'custom' && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 12, flexWrap: 'wrap' }}>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                style={inputStyle(isDark, borderColor)} />
              <span style={{ color: subText }}>→</span>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                style={inputStyle(isDark, borderColor)} />
              <button onClick={applyCustomRange} style={btnStyle(isDark, accent)}>
                Apply
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
        <SummaryCard
          icon={<DollarSign size={22} />}
          title="Total COD Orders"
          value={summary.total_cod_orders || 0}
          subtext={`Total Amount: ${fmt(summary.total_cod_amount)}`}
          color="#6c63ff"
          isDark={isDark}
        />
        <SummaryCard
          icon={<CheckCircle size={22} />}
          title="Collected"
          value={fmt(summary.collected_amount)}
          subtext={`${summary.collected_orders || 0} orders`}
          color="#10b981"
          isDark={isDark}
        />
        <SummaryCard
          icon={<Clock size={22} />}
          title="Pending Collection"
          value={fmt(summary.pending_amount)}
          subtext={`${summary.pending_collection_orders || 0} orders`}
          color="#f59e0b"
          isDark={isDark}
        />
        <SummaryCard
          icon={<Truck size={22} />}
          title="Driver Holding Cash"
          value={fmt(summary.driver_cash_holding)}
          subtext="Not yet deposited"
          color="#ef4444"
          isDark={isDark}
        />
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: `1px solid ${borderColor}`, marginBottom: 20 }}>
        {[
          { id: 'overview', label: 'Overview', icon: <TrendingUp size={16} /> },
          { id: 'daily', label: 'Day-wise', icon: <Calendar size={16} /> },
          { id: 'drivers', label: 'Driver-wise', icon: <Truck size={16} /> },
          { id: 'orders', label: 'Order Details', icon: <FileSpreadsheet size={16} /> },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{
              padding: '10px 16px',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === t.id ? `2px solid ${accent}` : '2px solid transparent',
              color: activeTab === t.id ? accent : subText,
              cursor: 'pointer',
              fontSize: 14, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: 16 }}>
          <div style={chartCardStyle(cardBg, borderColor)}>
            <h3 style={chartTitleStyle(textColor)}>Collection Status</h3>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Collected', value: Number(summary.collected_amount || 0) },
                    { name: 'Pending', value: Number(summary.pending_amount || 0) },
                  ]}
                  cx="50%" cy="50%" innerRadius={60} outerRadius={100}
                  dataKey="value" label={(e) => `${e.name}: ${fmt(e.value)}`}
                >
                  <Cell fill="#10b981" />
                  <Cell fill="#f59e0b" />
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: cardBg, border: `1px solid ${borderColor}`, color: textColor }}
                  formatter={(v) => fmt(v)}
                />
                <Legend wrapperStyle={{ color: textColor }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div style={chartCardStyle(cardBg, borderColor)}>
            <h3 style={chartTitleStyle(textColor)}>Daily Cash Flow</h3>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data?.dailyBreakdown?.slice(0, 14).reverse() || []}>
                <CartesianGrid strokeDasharray="3 3" stroke={borderColor} />
                <XAxis dataKey="date" stroke={subText} fontSize={11}
                  tickFormatter={(d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} />
                <YAxis stroke={subText} fontSize={11} />
                <RechartsTooltip
                  contentStyle={{ backgroundColor: cardBg, border: `1px solid ${borderColor}`, color: textColor }}
                  formatter={(v) => fmt(v)}
                />
                <Legend wrapperStyle={{ color: textColor }} />
                <Bar dataKey="collected_amount" stackId="a" fill="#10b981" name="Collected" />
                <Bar dataKey="pending_amount" stackId="a" fill="#f59e0b" name="Pending" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {activeTab === 'daily' && (
        <div style={tableCardStyle(cardBg, borderColor)}>
          <h3 style={chartTitleStyle(textColor)}>Day-wise Breakdown</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${borderColor}` }}>
                  <th style={thStyle(textColor)}>Date</th>
                  <th style={thStyle(textColor)}>Orders</th>
                  <th style={thStyle(textColor)}>Total Amount</th>
                  <th style={thStyle(textColor)}>Collected</th>
                  <th style={thStyle(textColor)}>Pending</th>
                </tr>
              </thead>
              <tbody>
                {(data?.dailyBreakdown || []).map((d, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${borderColor}` }}>
                    <td style={tdStyle(textColor)}>{new Date(d.date).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</td>
                    <td style={tdStyle(textColor)}>{d.total_orders}</td>
                    <td style={tdStyle(textColor)}>{fmt(d.total_amount)}</td>
                    <td style={{ ...tdStyle(textColor), color: '#10b981' }}>
                      {fmt(d.collected_amount)} ({d.collected_count})
                    </td>
                    <td style={{ ...tdStyle(textColor), color: '#f59e0b' }}>
                      {fmt(d.pending_amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'drivers' && (
        <div style={tableCardStyle(cardBg, borderColor)}>
          <h3 style={chartTitleStyle(textColor)}>Driver-wise Breakdown — Kitna Cash Collect Hua</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${borderColor}` }}>
                  <th style={thStyle(textColor)}>Driver</th>
                  <th style={thStyle(textColor)}>Contact</th>
                  <th style={thStyle(textColor)}>COD Orders</th>
                  <th style={thStyle(textColor)}>Total Amount</th>
                  <th style={thStyle(textColor)}>Collected</th>
                  <th style={thStyle(textColor)}>Pending</th>
                </tr>
              </thead>
              <tbody>
                {(data?.driverBreakdown || []).map((d, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${borderColor}` }}>
                    <td style={tdStyle(textColor)}>{d.driver_name || 'Unassigned'}</td>
                    <td style={tdStyle(textColor)}>{d.driver_contact || '-'}</td>
                    <td style={tdStyle(textColor)}>{d.total_cod_orders}</td>
                    <td style={tdStyle(textColor)}>{fmt(d.total_amount)}</td>
                    <td style={{ ...tdStyle(textColor), color: '#10b981' }}>{fmt(d.collected_amount)}</td>
                    <td style={{ ...tdStyle(textColor), color: '#ef4444' }}>{fmt(d.pending_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'orders' && (
        <div style={tableCardStyle(cardBg, borderColor)}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <h3 style={{ ...chartTitleStyle(textColor), margin: 0 }}>Order Details (COD)</h3>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ position: 'relative' }}>
                <Search size={16} style={{ position: 'absolute', left: 10, top: 11, color: subText }} />
                <input
                  placeholder="Search order ID, driver, restaurant..."
                  value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ ...inputStyle(isDark, borderColor), paddingLeft: 32, minWidth: 280 }}
                />
              </div>
              {selectedOrders.length > 0 && (
                <button onClick={() => setShowDepositModal(true)}
                  style={btnStyle(isDark, '#10b981')}>
                  <CheckCircle size={16} />
                  Mark {selectedOrders.length} as Deposited
                </button>
              )}
              <button onClick={selectAllPending} style={btnStyle(isDark, '#6c63ff')}>
                Select All Pending
              </button>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${borderColor}` }}>
                  <th style={thStyle(textColor, 40)}></th>
                  <th style={thStyle(textColor)}>Order ID</th>
                  <th style={thStyle(textColor)}>Date</th>
                  <th style={thStyle(textColor)}>Driver</th>
                  <th style={thStyle(textColor)}>Restaurant</th>
                  <th style={thStyle(textColor)}>Amount</th>
                  <th style={thStyle(textColor)}>Status</th>
                  <th style={thStyle(textColor)}>Collected</th>
                </tr>
              </thead>
              <tbody>
                {(data?.orderDetails || [])
                  .filter(o => {
                    if (!searchTerm) return true;
                    const q = searchTerm.toLowerCase();
                    return (
                      String(o.order_id).includes(q) ||
                      (o.driver_name || '').toLowerCase().includes(q) ||
                      (o.restaurant_name || '').toLowerCase().includes(q) ||
                      (o.receiver_name || '').toLowerCase().includes(q)
                    );
                  })
                  .map((o) => {
                    const isCollected = !!o.cash_collected_at;
                    const isCancelled = ['cancelled', 'declined'].includes(o.order_status_driver);
                    const selectable = !isCollected && !isCancelled;

                    return (
                      <tr key={o.order_id} style={{ borderBottom: `1px solid ${borderColor}` }}>
                        <td style={tdStyle(textColor)}>
                          {selectable && (
                            <input
                              type="checkbox"
                              checked={selectedOrders.includes(o.order_id)}
                              onChange={() => toggleOrderSelection(o.order_id)}
                            />
                          )}
                        </td>
                        <td style={{ ...tdStyle(textColor), fontFamily: 'monospace', fontWeight: 600 }}>#{o.order_id}</td>
                        <td style={tdStyle(textColor)}>{new Date(o.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                        <td style={tdStyle(textColor)}>{o.driver_name || '—'}</td>
                        <td style={tdStyle(textColor)}>{o.restaurant_name || '—'}</td>
                        <td style={{ ...tdStyle(textColor), fontWeight: 600 }}>{fmt(o.final_amount)}</td>
                        <td style={tdStyle(textColor)}>
                          <span style={{
                            padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                            backgroundColor: isCancelled ? '#fee2e2' : (isCollected ? '#dcfce7' : '#fef3c7'),
                            color: isCancelled ? '#991b1b' : (isCollected ? '#166534' : '#92400e'),
                          }}>
                            {isCancelled ? 'Cancelled' : (isCollected ? 'Collected' : 'Pending')}
                          </span>
                        </td>
                        <td style={tdStyle(textColor)}>
                          {o.cash_collected_at ? new Date(o.cash_collected_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Deposit Modal */}
      {showDepositModal && (
        <div style={modalOverlayStyle} onClick={() => setShowDepositModal(false)}>
          <div style={modalContentStyle(cardBg, textColor)} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0 }}>Mark Cash as Deposited</h3>
              <button onClick={() => setShowDepositModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: textColor }}>
                <X size={20} />
              </button>
            </div>
            <p style={{ color: subText, marginBottom: 16 }}>
              You're about to mark <strong>{selectedOrders.length}</strong> order(s) as cash deposited.
            </p>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Deposit Mode</label>
            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
              {['CASH', 'ONLINE'].map(m => (
                <button key={m} onClick={() => setDepositMode(m)}
                  style={{
                    flex: 1, padding: '10px', borderRadius: 8,
                    backgroundColor: depositMode === m ? accent : 'transparent',
                    color: depositMode === m ? '#fff' : textColor,
                    border: `1px solid ${depositMode === m ? accent : borderColor}`,
                    cursor: 'pointer', fontWeight: 600,
                  }}>
                  {m}
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setShowDepositModal(false)}
                style={{ ...btnStyle(isDark, subText), flex: 1 }}>
                Cancel
              </button>
              <button onClick={markAsDeposited}
                style={{ ...btnStyle(isDark, '#10b981'), flex: 1 }}>
                Confirm Deposit
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
};

// ====================== HELPER COMPONENTS ======================
const SummaryCard = ({ icon, title, value, subtext, color, isDark }) => (
  <div style={{
    backgroundColor: isDark ? '#242842' : '#ffffff',
    borderRadius: 12, padding: 20,
    border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
    boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.04)',
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
      <div style={{
        width: 44, height: 44, borderRadius: 10,
        backgroundColor: color + '20', color, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}>{icon}</div>
    </div>
    <div style={{ fontSize: 13, color: isDark ? '#9ca3af' : '#6b7280', marginBottom: 4 }}>{title}</div>
    <div style={{ fontSize: 24, fontWeight: 700, color: isDark ? '#e5e7eb' : '#1f2937' }}>{value}</div>
    <div style={{ fontSize: 12, color: isDark ? '#9ca3af' : '#6b7280', marginTop: 4 }}>{subtext}</div>
  </div>
);

// ====================== STYLES ======================
const btnStyle = (isDark, color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', borderRadius: 8,
  backgroundColor: color, color: '#fff',
  border: 'none', cursor: 'pointer',
  fontSize: 13, fontWeight: 600,
});

const inputStyle = (isDark, borderColor) => ({
  padding: '8px 12px', borderRadius: 8,
  backgroundColor: isDark ? '#1a1d2e' : '#fff',
  color: isDark ? '#e5e7eb' : '#1f2937',
  border: `1px solid ${borderColor}`,
  fontSize: 13, outline: 'none',
});

const chartCardStyle = (bg, border) => ({
  backgroundColor: bg, borderRadius: 12, padding: 20,
  border: `1px solid ${border}`,
});

const tableCardStyle = (bg, border) => ({
  backgroundColor: bg, borderRadius: 12, padding: 20,
  border: `1px solid ${border}`,
});

const chartTitleStyle = (color) => ({
  margin: '0 0 16px', fontSize: 16, fontWeight: 700, color,
});

const thStyle = (color, w) => ({
  padding: '12px 10px', textAlign: 'left',
  fontSize: 12, fontWeight: 700, color,
  textTransform: 'uppercase', letterSpacing: 0.5,
  width: w,
});

const tdStyle = (color) => ({
  padding: '10px', fontSize: 13, color,
});

const modalOverlayStyle = {
  position: 'fixed', inset: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000,
};

const modalContentStyle = (bg, color) => ({
  backgroundColor: bg, borderRadius: 12, padding: 24,
  width: 420, maxWidth: '90vw', color,
});

export default CashMoney;